import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { api, ApiError, files } from '../lib/api';
import '../styles/templates.css';

const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW);
const linkFor = (url: string): string => `${window.location.origin}${PREVIEW ? '/#' : ''}${url}`;

async function copy(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

export function ShareModal({ docId, docNumber, docTypeLabel, onClose }: { docId: string; docNumber: string; docTypeLabel: string; onClose: () => void }) {
  const [token, setToken] = useState<string | null>(null);
  const [url, setUrl] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.post<{ token: string; url: string }>(`/documents/${docId}/share`, {})
      .then((r) => { setToken(r.token); setUrl(r.url); })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'No se pudo generar el enlace.'));
  }, [docId]);

  const link = url ? linkFor(url) : '';
  const message = `Hello, sharing ${docTypeLabel.toLowerCase()} ${docNumber} with you. You can view it and download the PDF here: ${link}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent(`${docTypeLabel} ${docNumber}`)}&body=${encodeURIComponent(message)}`;

  const doCopy = async () => { if (await copy(link)) { setCopied(true); setTimeout(() => setCopied(false), 1800); } };
  const doPdf = async () => {
    if (PREVIEW) { window.open(link, '_blank'); return; }
    try { await files.download(`/documents/${docId}/pdf`, `${docNumber}.pdf`); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo descargar el PDF.'); }
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal__head">
          <div>
            <span className="eyebrow">Compartir</span>
            <h2 className="h2" style={{ marginTop: 4, fontSize: 19 }}>{docTypeLabel} {docNumber}</h2>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>

        <div className="modal__body">
          {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
          {!err && !token && <div className="faint" style={{ padding: 20, textAlign: 'center' }}>Generando enlace…</div>}

          {token && (
            <>
              <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
                Anyone with this link can view the document and download its branded PDF. No account needed.
              </p>

              <div className="link-box" style={{ marginBottom: 16 }}>
                <div className="link-box__row">
                  <span className="link-box__label">Enlace</span>
                  <span className="link-box__value">{link}</span>
                  <button className="copy-btn" onClick={doCopy} title="Copiar">
                    <Icon name={copied ? 'check' : 'copy'} size={15} />
                  </button>
                </div>
              </div>

              <div className="share-actions">
                <a className="share-btn share-btn--wa" href={waUrl} target="_blank" rel="noreferrer">
                  <span className="share-btn__ico"><Icon name="whatsapp" size={18} /></span>
                  <span>WhatsApp<small>Enviar por chat</small></span>
                </a>
                <a className="share-btn share-btn--mail" href={mailUrl}>
                  <span className="share-btn__ico"><Icon name="mail" size={17} /></span>
                  <span>Email<small>Abrir en tu cliente</small></span>
                </a>
                <button className="share-btn share-btn--copy" onClick={doCopy}>
                  <span className="share-btn__ico"><Icon name={copied ? 'check' : 'copy'} size={16} /></span>
                  <span>{copied ? 'Copiado' : 'Copiar enlace'}<small>Al portapapeles</small></span>
                </button>
                <button className="share-btn share-btn--pdf" onClick={doPdf}>
                  <span className="share-btn__ico"><Icon name="download" size={16} /></span>
                  <span>Descargar PDF<small>{PREVIEW ? 'Abrir vista' : 'Documento con marca'}</small></span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
